import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMe } from '../auth/session';
import { LogoMark } from '../components/Logo';
import { formatCountdown, maintenanceView, type MaintenanceStatus, parseStatus, pollIntervalMs } from '../lib/maintenance';
import { useIsBusy } from './busy';
import styles from './MaintenanceLayer.module.css';

/**
 * The deploy's maintenance, as the open app sees it (deploy/server/maintenance.sh):
 * - announced: signed-in users get a banner counting down, and can finish what they are doing;
 *   anyone not signed in gets the notice at once (Caddy also answers them with it), so no new
 *   session starts;
 * - maintenance: the notice covers the app, but only once no take or playback is running
 *   (useBusy); until then a banner says it will follow;
 * - over: a page that showed the maintenance reloads, to the new version (and the login page,
 *   since a deploy ends the sessions).
 * Deploys without downtime never announce anything.
 */
export function MaintenanceLayer() {
  const { t } = useTranslation();
  const me = useMe().data;
  const busy = useIsBusy();
  const [status, setStatus] = useState<MaintenanceStatus>({ phase: 'none' });
  const [skewMs, setSkewMs] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const sawMaintenance = useRef(false);
  const view = maintenanceView(status, now, skewMs);

  // Read the announcement: every 30 s, every 5 s while something is coming, and when the tab comes back.
  useEffect(() => {
    let cancelled = false;
    const read = async () => {
      try {
        const response = await fetch('/maintenance.json', { cache: 'no-store', credentials: 'same-origin' });
        const date = response.headers.get('Date');
        const json: unknown = response.ok ? await response.json().catch(() => null) : null;
        if (cancelled) return;
        if (date) setSkewMs(Date.parse(date) - Date.now());
        setStatus(parseStatus(json));
      } catch {
        // Offline or the server is changing hands: keep what we know.
      }
    };
    void read();
    const timer = window.setInterval(() => void read(), pollIntervalMs(view));
    const onVisible = () => {
      if (document.visibilityState === 'visible') void read();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [view.phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // The countdown ticks every second while a maintenance is announced.
  useEffect(() => {
    if (status.phase !== 'announced') return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [status.phase]);

  // When a maintenance this page saw is over, load the new version (not in the middle of a take).
  useEffect(() => {
    if (view.phase === 'maintenance') sawMaintenance.current = true;
    if (view.phase === 'none' && sawMaintenance.current && !busy) window.location.reload();
  }, [view.phase, busy]);

  if (view.phase === 'none') return null;
  const signedIn = !!me;
  const notice = (
    <div className={styles.overlay} role="alertdialog" aria-modal="true" aria-labelledby="maintenance-title" data-testid="maintenance-notice">
      <div className={styles.card}>
        <LogoMark size={56} />
        <h1 id="maintenance-title" className={styles.title}>
          {t('maintenance.title')}
        </h1>
        <p className={styles.text}>{signedIn ? t('maintenance.bodyOpen') : t('maintenance.body')}</p>
      </div>
    </div>
  );
  if (!signedIn) return notice;
  if (view.phase === 'maintenance' && !busy) return notice;
  return (
    <div className={styles.banner} role="status" data-testid="maintenance-banner" data-phase={view.phase}>
      {view.phase === 'announced'
        ? t('maintenance.announced', { time: formatCountdown(view.secondsLeft ?? 0) })
        : t('maintenance.waiting')}
    </div>
  );
}
